import {
  MaterialReactTable,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  MRT_ColumnOrderState as ColumnOrderState,
  useMaterialReactTable,
  MRT_Row,
  MRT_ColumnDef,
  getMRT_RowSelectionHandler
} from 'material-react-table';
import { SearchResults, SearchSort, SearchSortField } from 'types/Search';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import OpenInNew from '@mui/icons-material/OpenInNew';
import SearchOverview from '../SearchOverview/SearchOverview';
import { ProductType } from 'types/Products';
import useRowSelection from './hooks/useRowSelection';
import useTableScroll from './hooks/useTableScroll';
import { BackToTopButton } from './BackToTopButton';
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { SearchViewPropsExtended } from './types';
import { SegmentTag } from 'types/global';
import { RowBanner } from './RowBanner';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { MarketingBannerProps } from '../MarketingBanner';
import { useAuth } from 'react-oidc-context';

type SearchTableProps<T extends object> = {
  searchResults: SearchResults;
  onLoadMore: () => void;
  loading: boolean;
  error: boolean;
  compareModeActive: boolean;
  requestsModeActive: boolean;
  logItemClick: (document: T) => void;
  sortOption: SearchSort[];
  handleSortChange: (option: { value: SearchSortField }) => void;
  columns: MRT_ColumnDef<T, unknown>[];
  generateItemPageLink: (row: MRT_Row<T>) => string;
  renderRowActions?: (row: MRT_Row<T>) => React.ReactNode;
  getRowId: (row: T) => string;
  setShowRegisterModal?: (a: boolean) => void;
  enableMarketingBanner?: boolean;
  columnManagement?: {
    columnOrder: ColumnOrderState;
    setColumnOrder: (
      updater: ColumnOrderState | ((prev: ColumnOrderState) => ColumnOrderState)
    ) => void;
    columnVisibility: Record<string, boolean>;
    setColumnVisibility: Dispatch<SetStateAction<Record<string, boolean>>>;
  };
  type?: 'PRODUCTS' | 'SUPPLIERS';
} & SearchViewPropsExtended<T>;

export const SearchTable = <T extends object>({
  sortOption,
  handleSortChange,
  getRowId,
  searchResults,
  error,
  loading,
  compareModeActive,
  requestsModeActive,
  selectionModeEnabled,
  canSelectMore,
  setShowRegisterModal,
  onSelectedChange,
  onLoadMore,
  enableMarketingBanner,
  logItemClick,
  columns,
  generateItemPageLink,
  columnManagement,
  type = 'PRODUCTS'
}: SearchTableProps<T>) => {
  const navigate = useNavigate();
  const {
    onScroll,
    rowVirtualizerInstanceRef,
    handleScrollToTop,
    showBackToTop
  } = useTableScroll(loading, searchResults, onLoadMore);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [tableWidth, setTableWidth] = useState(0);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      if (tableContainerRef.current) {
        setTableWidth(tableContainerRef.current.offsetWidth);
      }
    });

    const currentRef = tableContainerRef.current;

    if (currentRef) {
      resizeObserver.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        resizeObserver.unobserve(currentRef);
      }
    };
  }, []);
  const data = useMemo(
    () => searchResults.results.map(row => row.document),
    [searchResults.results]
  );
  const { setRowSelection, rowSelection } = useRowSelection(
    data,
    compareModeActive,
    requestsModeActive
  );
  const {
    searchMarketingBannerV2
  }: { searchMarketingBannerV2: MarketingBannerProps } = useFlags();
  const { isAuthenticated } = useAuth();
  const isBannerIndex = (index: number) =>
    index === 6 || (index > 6 && (index - 6) % 10 === 0);
  const table = useMaterialReactTable<T>({
    enableRowVirtualization: true,
    enableTopToolbar: false,
    enableRowSelection: !selectionModeEnabled
      ? false
      : (row: { getIsSelected: () => boolean }) =>
          row.getIsSelected() || canSelectMore,

    enableSelectAll: false,
    enableColumnDragging: false,
    enablePinning: true,
    enableSorting: false,
    enableColumnActions: false,
    enableStickyHeader: true,
    enablePagination: false,
    rowCount: searchResults.total,
    positionActionsColumn: 'last',
    enableRowActions: true,
    muiSelectCheckboxProps: ({ row }) => ({
      size: 'medium',
      onClick: () => {
        onSelectedChange(row.original, !row.getIsSelected());
      }
    }),
    displayColumnDefOptions: {
      'mrt-row-actions': {
        size: 30,
        grow: false,
        header: ''
      },
      'mrt-row-select': {
        size: columnManagement ? 30 : 80,
        header: ''
      }
    },
    onRowSelectionChange: setRowSelection,
    muiTablePaperProps: {
      elevation: 0,
      variant: 'outlined',
      sx: {
        flex: '1 1 auto',
        display: 'flex',
        flexDirection: 'column',
        flexFlow: 'column'
      }
    },
    muiTableHeadCellProps: {
      sx: {
        borderBottom: ({ palette }) => `1px solid ${palette.primary.main}`,
        '.Mui-TableHeadCell-Content-Wrapper': {
          whiteSpace: 'nowrap',
          fontWeight: 'normal',
          color: ({ palette }) => palette.text.secondary,
          fontSize: ({ typography }) => typography.body1.fontSize,
          py: 0.5
        }
      }
    },

    muiTableContainerProps: {
      sx: {
        display: 'flex',
        flexDirection: 'column',
        flex: { xs: '1 1 100%', sm: '1 1 0' },
        ...(enableMarketingBanner && {
          '.Mui-TableBodyCell-DetailPanel': {
            border: 'none',
            margin: 0,
            padding: 0,
            width: '100%',
            height: 'auto'
          }
        })
      },
      onScroll: event => onScroll(event.target as HTMLDivElement)
    },
    muiSkeletonProps: {
      height: 42
    },
    muiTableBodyRowProps: ({ row, staticRowIndex, table }) => ({
      component: 'tr',
      onClick: (event: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => {
        if (enableMarketingBanner && isBannerIndex(row.index)) return;
        if (selectionModeEnabled && (row.getIsSelected() || canSelectMore)) {
          getMRT_RowSelectionHandler({ row, staticRowIndex, table })(event);
          onSelectedChange(row.original, !row.getIsSelected());
        } else if (!selectionModeEnabled) {
          logItemClick(row.original);
          navigate(generateItemPageLink(row));
        }
      },
      sx: {
        cursor: 'pointer',
        '&:last-child td': {
          borderBottom: 'none'
        }
      }
    }),

    muiTableHeadProps: {
      sx: {
        borderBottom: 'none'
      }
    },

    muiBottomToolbarProps: {
      sx: {
        height: 4,
        minHeight: 0
      }
    },

    rowVirtualizerInstanceRef,
    rowVirtualizerOptions: { overscan: 1 },
    ...(enableMarketingBanner && {
      enableExpandAll: false,
      muiExpandButtonProps: () => ({
        children: null
      }),
      renderDetailPanel: ({ row }) =>
        enableMarketingBanner && isBannerIndex(row.index) ? (
          <Box
            sx={{
              maxWidth: tableWidth,
              position: 'sticky',
              flex: 1,
              top: 0,
              left: 0
            }}
          >
            <RowBanner
              isAuthenticated={isAuthenticated}
              searchMarketingBannerProps={searchMarketingBannerV2}
              segment={searchResults.search?.segment ?? SegmentTag.PERSONALCARE}
              setShowRegisterModal={a => setShowRegisterModal?.(a)}
            />
          </Box>
        ) : null
    }),

    getRowId: (row: T) => getRowId(row),
    initialState: {
      expanded: true,
      density: 'compact',
      columnPinning: { right: ['mrt-row-actions'] },
      columnOrder: columnManagement?.columnOrder ?? [
        'mrt-row-select',
        'bookmarked',
        'mrt-row-actions'
      ],
      columnVisibility: {
        ...columnManagement?.columnVisibility,
        ...(enableMarketingBanner && { 'mrt-row-expand': false })
      }
    },
    enableColumnOrdering: !!columnManagement,
    ...(columnManagement && {
      onColumnOrderChange: columnManagement.setColumnOrder,
      onColumnVisibilityChange: columnManagement.setColumnVisibility
    }),

    enableBottomToolbar: loading,
    renderRowActions: ({ row }) => (
      <Tooltip
        title={type === 'PRODUCTS' ? 'Open Product Page' : 'Open Company Page'}
      >
        <Box sx={{ minHeight: 60, display: 'flex', alignItems: 'center' }}>
          <IconButton
            color="primary"
            sx={{ paddingRight: 8 }}
            onClick={() => {
              logItemClick(row.original);
              navigate(generateItemPageLink(row));
            }}
          >
            <OpenInNew color="primary" fontSize="medium" />
          </IconButton>
        </Box>
      </Tooltip>
    ),
    state: {
      isLoading: !searchResults.search,
      showAlertBanner: error,
      showProgressBars: loading,
      rowSelection,
      columnVisibility: {
        ...(columnManagement?.columnVisibility ?? {}),
        'mrt-row-expand': false,
        bookmarked: !selectionModeEnabled
      },
      ...(columnManagement
        ? {
            columnOrder: columnManagement.columnOrder
          }
        : {})
    },
    columns,
    data: data as T[]
  });

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        maxWidth: '100%',
        overflow: 'auto',
        position: 'relative'
      }}
      ref={tableContainerRef}
    >
      <Stack
        sx={{
          flexDirection: 'row',
          gap: 2,
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <SearchOverview
          total={searchResults.total}
          target={
            type === 'SUPPLIERS'
              ? 'SUPPLIERS'
              : searchResults.search?.target ?? ProductType.INGREDIENTS
          }
          selectedOption={
            sortOption?.[0] ?? {
              value: SearchSortField.SCORE,
              label: 'Relevance'
            }
          }
          onSortChangeHandler={handleSortChange}
        />
        {columnManagement && (
          <Stack direction="row" alignItems={'center'}>
            <Typography
              className="search-overview-sort"
              sx={{
                whiteSpace: 'nowrap',
                fontWeight: 'bold',
                '&&': { display: { xs: 'none', md: 'flex' } }
              }}
            >
              Column Visibility
            </Typography>
            <ShowHideColumnsButton
              table={table}
              size="small"
              disableRipple
              sx={{ height: 24 }}
            />
          </Stack>
        )}
      </Stack>
      <MaterialReactTable table={table} />
      {showBackToTop && !loading && (
        <BackToTopButton handleScrollToTop={handleScrollToTop} />
      )}
    </Box>
  );
};
